// Based on ISO/IEC 5218 standard for gender codification.
export enum Gender {
  male = 'male',
  female = 'female',
}

export enum MassUnit {
  // dividing by this will cause an INF or NAN result, thus exposing the problem more readily
  unknown = 0,
  ug = 1e-6,
  mg = 0.001,
  g = 1,
  kg = 1000,
  // avoirdupois pound
  lb = 453.59237,
  // avoirdupois ounce
  oz = 28.3495,
  // imperial stone = 14 lb
  st = 6350.29318,
  not_applicable = -1,
}

export enum LengthUnit {
  // dividing by this will cause an INF or NAN result, thus exposing the problem more readily
  unknown = 0,
  mm = 0.001,
  cm = 0.01,
  m = 1,
  in = 0.0254,
  ft = 0.3048,
  not_applicable = -1,
}

//
export enum EnergyUnit {
  // dividing by this will cause an INF or NAN result, thus exposing the problem more readily
  unknown = 0,
  kcal = 1,
  kJ = 0.239006,
  not_applicable = -1,
}

export enum VolumeUnit {
  unknown = 0,
  ml = 1,
  fl_oz = 29.57,
  qt = 9463.53,
  pt = 473.18,
  cup = 240,
  tbsp = 14.8,
  tsp = 4.929,
}

export enum NutritionLabelStyle {
  EU = 'EU',
  US = 'US',
}

export enum DateFormat {
  Automatic = 'auto',
  US = 'M/d/yy',
  UK = 'd/M/yy',
  EUDot = 'd.M.yy',
}

export enum BFMethod {
  Caliper = 'caliper',
  Visual_OBSOLETE = 'visual',
  Tape = 'tape',
  Self = 'self',
}

export enum ActivityLevel {
  Sedentary = 100,
  SomewhatActive = 200,
  Active = 300,
  VeryActive = 400,
}

export enum SleepQuality {
  Poor = 100,
  Average = 200,
  Great = 300,
}

// note: HighStress is a smaller number than MildStress, but that's to be in line with other enums, where bigger is always better
export enum StressLevel {
  HighStress = 100,
  Average = 200,
  MildStress = 300,
}

export enum TrainingGoal {
  Automatic = 100,
  MuscleGrowth = 200,
  FatLoss = 300,
}

export enum DedicationLevel {
  SlowAndSteady = 100,
  Balanced = 200,
  MaximumEffort = 300,
}

export enum AdvancedMethodsMyoReps {
  Avoid = 100,
  Automatic = 200,
  Prefer = 300,
}


export enum AdvancedMethodsRMTesting {
  Avoid = 100,
  Automatic = 200,
  Prefer = 300,
}

export enum SetType {
  // First set on RM testing day.
  RMTest = 'rmtest',
  // Followup sets on RM testing day.
  RMTestFollow = 'rmtest_follow',
  // First set on rep testing day.
  RepTest = 'reptest',
  // First set of on test day which has a RM test disabled
  NonRMTest = 'nonrmtest',
  // Regular workout, 1st set, planned.
  Regular1st = 'regular1st',
  // A regular followup set (happens _both_ after Regular1st and RepTest sets).
  Followup = 'followup',
  // A deload set.
  Deload = 'deload',
  // A drop set.
  Dropset = 'dropset',
  // A set that was skipped due to volume downregulation
  Downregulated = 'downregulated',
  Dummy = 'dummy',
}

export enum SetSuccess {
  Ok = 'ok' /** used by testing sets, and others that don't have a target */,
  MinReps = 'min',
  TargetReps = 'target',
  Fail = 'fail',
}

export enum PostSetStatus {
  Weakness = 'weakness',
  NonRMStrengthTest = 'non_rm_strength_test',
  StrengthTestSuccess = 'strength_test_success',
  StrengthTestSuccessLastSet = 'strength_test_success_last_set',
  StrengthTestFailToManyReps = 'strength_test_fail_too_many_reps',
  StrengthTestFailToManyRepsLastSet = 'strength_test_fail_too_many_reps_last_set',
  StrengthTestFailToFewReps = 'strength_test_fail_too_few_reps',
  StrengthTestFailToFewRepsLastSet = 'strength_test_fail_too_many_few_reps_last_set',
  RepTestSuccess = 'rep_test_success',
  RepTestSuccessLastSet = 'rep_test_success_last_set',
  // The two directions of the same answer: the user said the prescribed rep-test weight was way
  // off. Before #4558 one answer covered both and everyone was told they had said 'too heavy'.
  RepTestFailToHeavy = 'rep_test_fail_too_heavy',
  RepTestFailToLight = 'rep_test_fail_too_light',
  RepTestBadEquipment = 'rep_test_bad_equipment',
  ProgressionDeload = 'progression_deload',
  // #4822: the deload taken on an exercise's LAST set, where no lighter sets follow in this
  // session. Its whole effect is the next workout's one-step lower target weight, so its message
  // says that instead of "Deloading...".
  ProgressionDeloadLastSet = 'progression_deload_last_set',
  UpdatedRepTargetRegularSession = 'updated_rep_target_regular_session',
  UpdatedRepTargetHighRepSession = 'updated_rep_target_high_rep_session',
  UpdatedRepTargetHeavySession = 'updated_rep_target_heavy_session',
  SkippedSet = 'skipped_set',
  RegularSetLowerWeight = 'regular_set_lower_weight',
  ImmediateProgression = 'immediate_progression',
  ImmediateProgressionReps = 'immediate_progression_reps',
  MaxedOutEquipment = 'maxed_out_equipment',
  PlateauResolved = 'plateau_resolved',
  PlateauDetected = 'plateau_detected',
  PlateauDismissed = 'plateau_dismissed',
  VolumeAutoregulationIncrease = 'volume_autoregulation_increase',
  VolumeAutoregulationDecrease = 'volume_autoregulation_decrease',
  VolumeAutoregulationPending = 'volume_autoregulation_pending',
  // #5620: a follow-up after a passed strength test repeated the tested weight at the rep target. It
  // is a note only — the set is NOT scored a success; see isFollowupAtStrengthTestWeight.
  StrengthTestFollowupDone = 'strength_test_followup_done',
  // #5979: the rep target reached its ceiling of 30, the next weight step was too big a jump, and
  // the user was asked whether to switch exercise. One status per answer: switch, or keep 30.
  RepCeilingSwitch = 'rep_ceiling_switch',
  RepCeilingHeld = 'rep_ceiling_held',
}

export const remapOldPostSetStatusNumberToEnum: { [key: number]: PostSetStatus } = {
  [1]: PostSetStatus.Weakness,
  [2]: PostSetStatus.StrengthTestSuccess,
  [3]: PostSetStatus.StrengthTestSuccessLastSet,
  [4]: PostSetStatus.StrengthTestFailToManyReps,
  [5]: PostSetStatus.StrengthTestFailToManyRepsLastSet,
  [6]: PostSetStatus.RepTestSuccess,
  [7]: PostSetStatus.RepTestSuccessLastSet,
  [8]: PostSetStatus.RepTestFailToHeavy,
  [9]: PostSetStatus.RepTestBadEquipment,
  [10]: PostSetStatus.PlateauResolved,
  [11]: PostSetStatus.ProgressionDeload,
  [12]: PostSetStatus.UpdatedRepTargetRegularSession,
  [13]: PostSetStatus.UpdatedRepTargetHighRepSession,
  [14]: PostSetStatus.UpdatedRepTargetHeavySession,
  [15]: PostSetStatus.SkippedSet,
  [16]: PostSetStatus.RegularSetLowerWeight,
  [17]: PostSetStatus.ImmediateProgression,
  [18]: PostSetStatus.MaxedOutEquipment,
  [19]: PostSetStatus.PlateauResolved,
}

export enum PreSetStatus {
  DropSet = 'dropset',
  CompleteProgressionReset = 'complete_progression_reset',
  DidNotPerformDeload = 'did_not_perform_deload',
  DidNotPerformRetest = 'did_not_perform_retest',
  PlateauResolved = 'plateau_resolved',
  // Fires instead of DidNotPerformDeload when the layoff's weight cut would not show on the
  // equipment (low-weight exercises near the equipment floor, or since #5617 a stored target
  // already at or below the cut weight) — we apply the adjustment to the rep target instead,
  // so the toast wording has to switch too.
  DidNotPerformDeloadReps = 'did_not_perform_deload_reps',
  // #5176: the plan asked for a weight the user's own recent sets say they cannot lift for the
  // prescribed reps, so the app lowered it to something they can actually perform.
  PrescribedWeightCapped = 'prescribed_weight_capped',
  // #4822: the target weight is one step lower because the user accepted a deload on this
  // exercise's previous session at this intensity. Says that the set is a normal working set,
  // since a one-set exercise never shows the light explosive deload sets.
  DeloadedTargetWeight = 'deloaded_target_weight',
}

export const remapOldPreSetStatusNumberToEnum: { [key: number]: PreSetStatus } = {
  [1]: PreSetStatus.DropSet,
  [2]: PreSetStatus.CompleteProgressionReset,
  [3]: PreSetStatus.DidNotPerformDeload,
  [4]: PreSetStatus.DidNotPerformRetest,
  [5]: PreSetStatus.PlateauResolved,
  [6]: PreSetStatus.DidNotPerformDeloadReps,
}

export enum ImageSize {
  Thumbnail = 256,
  Small = 480,
  Medium = 720,
  Large = 1080,
}

export enum ButtonType {
  Header = 'header',
  Footer = 'toolbar',
  Fab = 'fab',
}

export enum BodyCompositionType {
  Contest = 1,
  Athletic,
  Average,
  Overweight,
  Obese,
}